import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, X } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { UpdatePositionOverrideSchema } from 'shared';

import {
  useDeletePositionOverride,
  useUpdatePositionOverride,
} from '../../features/positions/mutations';
import type { PositionDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';

import { UserConfirmedFlatForm } from './UserConfirmedFlatForm';

interface EditPositionDrawerProps {
  position: PositionDto | null;
  onOpenChange: (open: boolean) => void;
}

interface PositionOverrideFormValues {
  quantity: number;
  avgCost: number;
  notes: string;
}

export function EditPositionDrawer({ onOpenChange, position }: EditPositionDrawerProps) {
  const updateOverride = useUpdatePositionOverride();
  const deleteOverride = useDeletePositionOverride();
  const form = useForm<PositionOverrideFormValues>({
    defaultValues: {
      quantity: position ? Number(position.qty) : 0,
      avgCost: position ? Number(position.avgCost) : 0,
      notes: '',
    },
  });

  useEffect(() => {
    form.reset({
      quantity: position ? Number(position.qty) : 0,
      avgCost: position ? Number(position.avgCost) : 0,
      notes: '',
    });
  }, [form, position]);

  const open = Boolean(position);
  const onSubmit = form.handleSubmit((values) => {
    if (!position) return;
    const body = UpdatePositionOverrideSchema.parse(values);
    updateOverride.mutate(
      {
        accountId: position.accountId,
        symbol: position.symbol,
        body,
      },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60" />
        <Dialog.Content className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-xl flex-col border-l border-border bg-surface text-text shadow-xl sm:w-[32rem]">
          <div className="flex items-start justify-between gap-4 border-b border-border p-5">
            <div>
              <Dialog.Title className="text-lg font-semibold">Edit Position</Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-text-muted">
                {position?.shortProvenance === 'AMBIGUOUS_PREHISTORY'
                  ? `${position.symbol} position confirmation`
                  : position
                    ? `${position.symbol} manual override`
                    : 'Manual override'}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button aria-label="Close position editor" size="sm" type="button" variant="ghost">
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </Dialog.Close>
          </div>

          {position?.shortProvenance === 'AMBIGUOUS_PREHISTORY' ? (
            <UserConfirmedFlatForm position={position} onSuccess={() => onOpenChange(false)} />
          ) : (
            <form className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5" onSubmit={onSubmit}>
              <div className="flex gap-2 rounded border border-warn/70 bg-warn-subtle p-3 text-sm text-amber-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                <p>
                  This overrides computed FIFO values. The override will be lost if you reimport
                  trades for this symbol. Use this only to correct data errors.
                </p>
              </div>

              <FormField
                label="Quantity"
                type="number"
                step="0.000001"
                error={form.formState.errors.quantity?.message}
                {...form.register('quantity', {
                  valueAsNumber: true,
                  validate: (value) => value !== 0 || 'Quantity cannot be zero.',
                })}
              />
              <FormField
                label="Average cost"
                type="number"
                step="0.000001"
                error={form.formState.errors.avgCost?.message}
                {...form.register('avgCost', {
                  valueAsNumber: true,
                  min: { value: 0.000001, message: 'Average cost must be positive.' },
                })}
              />
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text" htmlFor="position-notes">
                  Reason for override
                </label>
                <textarea
                  id="position-notes"
                  className="min-h-28 w-full rounded border border-border bg-bg-muted px-3 py-2 text-sm text-text outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
                  aria-invalid={Boolean(form.formState.errors.notes)}
                  aria-describedby={
                    form.formState.errors.notes ? 'position-notes-error' : undefined
                  }
                  {...form.register('notes', {
                    minLength: { value: 20, message: 'Reason must be at least 20 characters.' },
                    required: 'Reason is required.',
                  })}
                />
                {form.formState.errors.notes ? (
                  <p id="position-notes-error" className="text-sm text-danger">
                    {form.formState.errors.notes.message}
                  </p>
                ) : null}
              </div>

              {updateOverride.isError || deleteOverride.isError ? (
                <p className="text-sm text-danger">Could not update manual override.</p>
              ) : null}

              <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={!position?.isManualOverride || deleteOverride.isPending}
                  onClick={() => {
                    if (!position) return;
                    deleteOverride.mutate(
                      { accountId: position.accountId, symbol: position.symbol },
                      { onSuccess: () => onOpenChange(false) },
                    );
                  }}
                >
                  {deleteOverride.isPending ? 'Removing...' : 'Remove override'}
                </Button>
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Dialog.Close asChild>
                    <Button type="button" variant="secondary">
                      Cancel
                    </Button>
                  </Dialog.Close>
                  <Button type="submit" disabled={updateOverride.isPending}>
                    {updateOverride.isPending ? 'Saving...' : 'Save override'}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
